import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../../../environments/environment';

export interface ReviewItemDto {
  reviewId: string;
  bookingId: string;
  rating: number;
  comment: string | null;
  status: 'published' | 'flagged' | 'hidden';
  flagCount: number;
  createdAt: string;
  updatedAt: string | null;
  author: {
    customerId: string | null;
    displayName: string;
    email: string | null;
    photoUrl: string | null;
  };
  response: {
    responseId: string;
    authorDisplayName: string | null;
    body: string;
    createdAt: string;
    updatedAt: string | null;
  } | null;
  staffId?: string | null;
  staffDisplayName?: string | null;
}

export interface ReviewsPageDto {
  items: ReviewItemDto[];
  total: number;
  page: number;
  pageSize: number;
}

interface SingleResponse<T> { isSuccess: boolean; value: T; }

/**
 * FAC-53 — wraps the convfact-api proxy for the booking-api admin reviews
 * endpoints. Used by the "Reseñas" section in the location settings form.
 */
@Injectable()
export class LocationReviewsService {
  private readonly baseUrl = `${environment.apiUrl}/api/v2/booking/merchant-locations`;

  constructor(private readonly http: HttpClient) { }

  listLocationReviews(locationId: string, opts: {
    status?: string | null;
    page?: number;
    pageSize?: number;
  } = {}): Observable<ReviewsPageDto> {
    let params = new HttpParams();
    if (opts.status) params = params.set('status', opts.status);
    if (opts.page) params = params.set('page', String(opts.page));
    if (opts.pageSize) params = params.set('pageSize', String(opts.pageSize));
    return this.http
      .get<SingleResponse<ReviewsPageDto>>(`${this.baseUrl}/${locationId}/reviews`, { params })
      .pipe(map(r => r.value));
  }

  listStaffReviews(locationId: string, opts: {
    staffId?: string | null;
    status?: string | null;
    page?: number;
    pageSize?: number;
  } = {}): Observable<ReviewsPageDto> {
    let params = new HttpParams();
    if (opts.staffId) params = params.set('staffId', opts.staffId);
    if (opts.status) params = params.set('status', opts.status);
    if (opts.page) params = params.set('page', String(opts.page));
    if (opts.pageSize) params = params.set('pageSize', String(opts.pageSize));
    return this.http
      .get<SingleResponse<ReviewsPageDto>>(`${this.baseUrl}/${locationId}/reviews/staff`, { params })
      .pipe(map(r => r.value));
  }

  respond(locationId: string, reviewId: string, body: string, authorDisplayName?: string | null): Observable<void> {
    return this.http
      .post<SingleResponse<unknown>>(`${this.baseUrl}/${locationId}/reviews/${reviewId}/respond`, {
        body, authorDisplayName, authorExternalId: null
      })
      .pipe(map(() => void 0));
  }

  deleteResponse(locationId: string, reviewId: string): Observable<void> {
    return this.http
      .delete<SingleResponse<unknown>>(`${this.baseUrl}/${locationId}/reviews/${reviewId}/respond`)
      .pipe(map(() => void 0));
  }

  setLocationReviewStatus(locationId: string, reviewId: string, status: 'published' | 'flagged' | 'hidden'): Observable<void> {
    return this.http
      .post<SingleResponse<unknown>>(`${this.baseUrl}/${locationId}/reviews/${reviewId}/status`, { status })
      .pipe(map(() => void 0));
  }

  setStaffReviewStatus(locationId: string, reviewId: string, status: 'published' | 'flagged' | 'hidden'): Observable<void> {
    return this.http
      .post<SingleResponse<unknown>>(`${this.baseUrl}/${locationId}/reviews/staff/${reviewId}/status`, { status })
      .pipe(map(() => void 0));
  }
}
